import { useState } from 'react';

import {
  ArShapeDetailsDrawer,
  BlastDesignContextCanvas,
  BlastDesignContextSideBar,
  BlockDetailsDrawer,
  DrilledHoleDetailsDrawer,
  DrillHoleDetailDrawer,
} from '../components';
import { BlastDesignContextProvider, useBlastDesignContext } from '../context';

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * Content of the Blast Design Context Viewer page, separated from the provider to ensure all components have access to context.
 * This is the main UI layout, including the sidebar, canvas, and details drawers.
 */
const BlastDesignContextPageContent = () => {
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const {
    selectedBlock,
    blockDrawerOpen,
    selectedHole,
    holeDrawerOpen,
    selectedDrillHole,
    drillHoleDrawerOpen,
    selectedArShape,
    arShapeDrawerOpen,
    handleDrawerClose,
  } = useBlastDesignContext();

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="relative flex h-full flex-col">
        <div className="relative flex min-h-0 flex-1 overflow-clip" ref={setContainerRef}>
          {/* Side Bar */}
          <BlastDesignContextSideBar />

          {/* Main Canvas */}
          <BlastDesignContextCanvas />

          {/* Details Drawers */}
          {containerRef && (
            <BlockDetailsDrawer
              container={containerRef}
              open={blockDrawerOpen}
              onClose={handleDrawerClose}
              block={selectedBlock}
            />
          )}
          {containerRef && (
            <DrilledHoleDetailsDrawer
              container={containerRef}
              open={holeDrawerOpen}
              onClose={handleDrawerClose}
              hole={selectedHole}
            />
          )}
          {containerRef && (
            <DrillHoleDetailDrawer
              container={containerRef}
              open={drillHoleDrawerOpen}
              onClose={handleDrawerClose}
              hole={selectedDrillHole}
            />
          )}
          {containerRef && (
            <ArShapeDetailsDrawer
              container={containerRef}
              open={arShapeDrawerOpen}
              onClose={handleDrawerClose}
              shape={selectedArShape}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Blast Design Context Viewer page - main component that sets up the context provider and renders the content.
 * The provider ensures all child components have access to shared state, queries, and handlers without prop drilling.
 */
const BlastDesignContextPage = () => (
  <BlastDesignContextProvider>
    <BlastDesignContextPageContent />
  </BlastDesignContextProvider>
);

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlastDesignContextPage };
